/**
 * This file contains the Material UI theme for the project
 */

import { Theme } from '@mui/material/styles';
import colors, { darkColors } from './colors';

const createTheme = (mode: 'light' | 'dark' = 'light') => {
    const isDarkMode = mode === 'dark';
    const currentColors = isDarkMode ? darkColors : colors;

    return {
        typography: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: 14,
            letterSpacing: 0.2,
            button: {
                textTransform: 'none' as any,
            } as any,
            h1: {
                fontSize: 28,
                fontWeight: 500, // 700,
                fontFamily: 'Madras',
                letterSpacing: 0.2,
            },
            h2: {
                fontSize: 18,
                fontWeight: 500, // 700,
                fontFamily: 'Madras',
                letterSpacing: 0.2,
            },
            h3: {
                fontSize: 16,
                fontWeight: 500, // 900,
                fontFamily: 'Madras',
                letterSpacing: 0.2,
            },
            h4: {
                fontSize: 14,
                fontWeight: 500, // 700,
                fontFamily: 'Madras',
                letterSpacing: 0.2,
            },
            subtitle1: {
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: 0.2,
            },
            subtitle2: {
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: 0.2,
            },
            body1: {
                lineHeight: 'inherit',
                letterSpacing: 0.2,
            },
            overline: {
                fontSize: 12,
                color: currentColors.lightBlack,
                textTransform: 'none' as any,
                lineHeight: '20px',
                letterSpacing: 0.2,
            },
        },
        components: {
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    divider: {
                        borderColor: `${currentColors.brightGray} !important`,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { boxShadow: 'unset!important' },
                    text: {
                        padding: '0px 8px',
                    },
                    containedSecondary: isDarkMode
                        ? {
                              backgroundColor: currentColors.blue,
                              color: currentColors.black,
                              '&:hover': {
                                  backgroundColor: colors.blue, // use light mode blue for hover in dark mode
                              },
                          }
                        : {},
                },
            },
            MuiDialog: {
                styleOverrides: {
                    root: {
                        // Content 1 over the page: #F2F3F5 at 20% in dark, #2F313F at 40% in light.
                        '& .MuiBackdrop-root': {
                            backgroundColor: currentColors.black,
                            opacity: isDarkMode ? '0.2!important' : '0.4!important',
                        },
                        // The dialog keeps MUI's elevation shadow in light mode; on the dark surface it
                        // reads as a murky halo instead of lift, so it is dropped there.
                        '& .MuiPaper-root': isDarkMode ? { boxShadow: 'unset' } : {},
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        padding: 12,
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: isDarkMode
                        ? {
                              '&:not(.Mui-checked) svg rect[fill="white"], &:not(.Mui-checked) svg rect[fill="#FFFFFF"], &:not(.Mui-checked) svg rect[fill="#fff"]':
                                  {
                                      fill: currentColors.white,
                                  },
                              '&.Mui-checked svg rect': {
                                  fill: `${currentColors.blue} !important`,
                                  stroke: 'transparent',
                              },
                          }
                        : {},
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    // Grey Chalk. MUI derives the header underline from `palette.divider`, which is a
                    // blue-tinted grey in dark mode and does not match the body rows' own border.
                    head: isDarkMode ? { borderBottom: `1px solid ${currentColors.brightGray}` } : {},
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        whiteSpace: 'normal !important' as any,
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    asterisk: {
                        display: 'none', // Hides the asterisk globally
                    },
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    input: {
                        '&:-webkit-autofill': {
                            WebkitBoxShadow: 'none !important',
                        },
                    },
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
            },
        },
        palette: {
            mode,
            primary: {
                main: currentColors.black,
                light: currentColors.lightBlack,
                contrastText: currentColors.white,
            },
            secondary: {
                main: isDarkMode ? currentColors.black : currentColors.blue,
                light: currentColors.lightBlue,
                contrastText: currentColors.white,
            },
            background: {
                default: currentColors.white,
                paper: currentColors.lightGray,
            },
            error: {
                main: currentColors.red,
                light: currentColors.red,
                dark: currentColors.red,
                contrastText: currentColors.darkWhite,
            },
            divider: currentColors.fillGrey,
            colors: {
                admin: currentColors.admin,
                border: currentColors.variantBlue,
                background: {
                    primaryElement: currentColors.brightGray,
                    secondaryElement: currentColors.lightGray,
                    tertiaryElement: currentColors.lightGray,
                    focusElement: currentColors.lightBlue,
                    filledForm: currentColors.brightGray,
                    filledFormHover: currentColors.darkerBrightGray,
                    separator: currentColors.brightGray,
                    header: currentColors.header,
                    appBar: currentColors.white,
                    primaryBtn: isDarkMode ? currentColors.black : currentColors.blue,
                    secondaryBtn: currentColors.white,
                    accentBtn: isDarkMode ? currentColors.blue : currentColors.lightBlue,
                    selected: currentColors.brightGray,
                    menuSeparator: currentColors.menuSeparator,
                    hover: currentColors.labelGrey,
                    lightRed: currentColors.lightRed,
                    lightGreen: currentColors.lightGreen,
                },
                text: {
                    primaryButton: currentColors.black,
                    secondaryButton: currentColors.brightGray,
                    overlineButton: currentColors.black,
                    error: currentColors.red,
                    warning: currentColors.orange,
                    success: currentColors.green,
                    contrast: currentColors.brightGray,
                    label: currentColors.labelGrey,
                    gold: currentColors.gold,
                    // Accent colour for links / text buttons.
                    accent: isDarkMode ? currentColors.black : currentColors.lightBlue,
                },
                icon: {
                    primary: currentColors.black,
                    lightGrey: isDarkMode ? currentColors.black : currentColors.iconGrey,
                },
                fixed: {
                    darkWhite: currentColors.darkWhite,
                    darkBlack: currentColors.darkBlack,
                },
            },
        },
        mixins: {
            toolbar: {
                minHeight: 96,
            },
        },
    };
};

// Ground for the call-the-driver button, which paints a hardcoded white icon and so needs to stay dark
// in both modes. `secondary.main` cannot be used: it is near-white in dark mode, which is how that
// button ended up white on white.
export const callButtonBackground = (theme: Theme): string =>
    theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.main;

const lightTheme = createTheme('light');
const darkTheme = createTheme('dark');

export default lightTheme;
export { lightTheme, darkTheme, createTheme };
